/**
 * #1731 (F-15, phase 3) — the ONE PiP voice session for the whole app.
 *
 * Port of cpv3 `features/chat-brand/pipVoiceStore.ts`. The session is a
 * module-level singleton, not a per-component hook: a session owned by the
 * chat page would hang up the call the moment the parent opened Settings.
 * `GlobalVoicePip` mounts once in `MainLayout`; `/chat` and the widget
 * subscribe to the same session through this store.
 *
 * Created lazily on first use — app boots that never touch voice never
 * construct it (and never load `livekit-client`, which the room adapter
 * dynamic-imports on connect).
 *
 * TRF-specific: the token route sets `voice:active:<user>` in Redis, and the
 * webhook that clears it cannot reach a dev box. `useVoiceSession` released
 * the key with an explicit DELETE on end; this store does the same on every
 * transition out of a minted session.
 */
import { useSyncExternalStore } from 'react';
import {
  createLiveKitRoom,
  createVoiceSession,
  isLive,
  type StartOptions,
  type TranscriptSegment,
  type VoiceSession,
  type VoiceSessionDeps,
  type VoiceSnapshot,
  type VoiceState,
} from '@core/voice/pip';
import { mergeSegment } from '@core/voice/pip/transcriptMessages';
import { resolveChatConfig } from '@chat-config';
import { voiceApi } from '@/services/voiceApi';

export type PipVoiceDepOverrides = Partial<VoiceSessionDeps> & {
  /** Releases the backend's active-session key. Defaults to `voiceApi.endSession`. */
  endSession?: () => Promise<void>;
};

let session: VoiceSession | null = null;
let transcript: TranscriptSegment[] = [];
let lastStart: StartOptions | undefined;
let overrides: PipVoiceDepOverrides = {};
const transcriptListeners = new Set<() => void>();

function notifyTranscript(): void {
  transcriptListeners.forEach((l) => l());
}

/** A state from which the backend has already minted (and keyed) a session. */
function minted(state: VoiceState): boolean {
  return state === 'connecting' || state === 'listening' || state === 'speaking';
}

/**
 * Test seam. Must run before the first `getPipVoiceSession()`; it also
 * resets a built session so a suite can rebuild with fakes.
 */
export function configurePipVoiceDeps(next: PipVoiceDepOverrides): void {
  overrides = next;
  if (session) {
    session.dispose();
    session = null;
  }
  transcript = [];
  lastStart = undefined;
}

export function __resetPipVoiceStoreForTests(): void {
  configurePipVoiceDeps({});
  transcriptListeners.clear();
}

export function getPipVoiceSession(): VoiceSession {
  if (!session) {
    const mint = overrides.mint ?? resolveChatConfig('companion').endpoints.mintVoiceGrant;
    if (!mint) {
      throw new Error('[#1731] the companion chat config must provide endpoints.mintVoiceGrant');
    }
    const endSession = overrides.endSession ?? voiceApi.endSession;
    const built = createVoiceSession({
      requestMic: overrides.requestMic ?? (() => navigator.mediaDevices.getUserMedia({ audio: true })),
      mint,
      openRoom: overrides.openRoom ?? createLiveKitRoom,
      now: overrides.now,
      agentTimeoutMs: overrides.agentTimeoutMs,
      slowAfterMs: overrides.slowAfterMs,
    });
    built.onTranscript((segment) => {
      transcript = mergeSegment(transcript, segment);
      notifyTranscript();
    });
    let previous = built.getSnapshot().state;
    built.subscribe(() => {
      const next = built.getSnapshot().state;
      if (next !== previous) {
        if ((next === 'ended' || next === 'failed') && minted(previous)) {
          void endSession().catch(() => {
            // Non-fatal: the key expires on its own (EX 3600) or the webhook clears it.
          });
        }
        previous = next;
      }
    });
    session = built;
  }
  return session;
}

/** Start a session, clearing the previous transcript window. */
export function startPipVoice(opts?: StartOptions): void {
  const s = getPipVoiceSession();
  // A mid-call wave click must not wipe the in-thread voice bubbles while
  // the call keeps running: only a start that can begin clears the window.
  if (isLive(s.getSnapshot().state)) return;
  transcript = [];
  notifyTranscript();
  lastStart = opts;
  void s.start(opts);
}

/** The widget's Retry: start again on the same thread the failed start named. */
export function retryPipVoice(): void {
  const s = getPipVoiceSession();
  s.reset();
  startPipVoice(lastStart);
}

function subscribeTranscript(listener: () => void): () => void {
  transcriptListeners.add(listener);
  return () => {
    transcriptListeners.delete(listener);
  };
}

export function usePipVoiceSnapshot(): VoiceSnapshot {
  const s = getPipVoiceSession();
  return useSyncExternalStore(s.subscribe, s.getSnapshot, s.getSnapshot);
}

export function usePipVoiceTranscript(): TranscriptSegment[] {
  return useSyncExternalStore(subscribeTranscript, () => transcript, () => transcript);
}
